import React, { useState, useEffect } from "react";
import { Modal, Typography, Card, Space, Input, Button, Alert, Spin, message, Empty } from "antd";
import { FileTextOutlined, SearchOutlined, CloseOutlined, ContainerOutlined, ReloadOutlined } from "@ant-design/icons";
import { useInvoiceReview } from "../../context/InvoiceReviewContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { settlementService } from "../../../../../api/enhancedApi";

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

const DocumentViewer = ({ visible, type, onClose }) => {
    const { invoiceDataSourceId } = useInvoiceReview();
    const [loading, setLoading] = useState(false);
    const [invoiceData, setInvoiceData] = useState(null);
    const [markdownContent, setMarkdownContent] = useState(null);

    useEffect(() => {
        if (visible && type === "invoice") {
            if (invoiceDataSourceId) {
                loadInvoiceData(invoiceDataSourceId);
            } else {
                // 如果没有 ID，尝试清空数据
                setInvoiceData(null);
                setMarkdownContent(null);
            }
        }
    }, [visible, type, invoiceDataSourceId]);

    const loadInvoiceData = async (id) => {
        setLoading(true);
        try {
            const response = await settlementService.getInvoiceDataSourceById(id);
            const data = response.data;
            setInvoiceData(data);

            // 提取 Markdown 内容
            let md = data?.structured_data?.ocr_result?.markdown_results;
            if (Array.isArray(md)) {
                md = md.join("\n\n");
            }
            setMarkdownContent(typeof md === "string" ? md : null);
        } catch (error) {
            console.error("加载账单数据失败:", error);
            message.error("加载账单数据失败: " + (error.message || "未知错误"));
        } finally {
            setLoading(false);
        }
    };

    const renderInvoiceContent = () => {
        if (loading) {
            return (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <Spin tip="正在加载账单数据..." />
                </div>
            );
        }

        if (!invoiceData) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <span>
                            未找到账单数据
                            {!invoiceDataSourceId && <div style={{ fontSize: 12, color: "#999" }}>URL中缺少 invoice_data_source_id 参数</div>}
                        </span>
                    }
                />
            );
        }

        return (
            <div>
                {/* 顶部提示 */}
                <Alert message="OCR 识别结果预览" description="以下内容为 AI 从原始账单文件中识别出的 Markdown 格式文本。您可以在此对照查看识别详情。" type="info" showIcon style={{ marginBottom: 16 }} />

                {/* 基本信息卡片 */}
                <Card size="small" title="账单基本信息" style={{ marginBottom: 16 }}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Text>
                            <strong>文件名:</strong> {invoiceData.original_filename}
                        </Text>
                        <Text>
                            <strong>数据源ID:</strong> {invoiceData.id}
                        </Text>
                        <Text>
                            <strong>状态:</strong> {invoiceData.status}
                        </Text>
                        <Text>
                            <strong>创建时间:</strong> {new Date(invoiceData.created_at).toLocaleString()}
                        </Text>
                    </Space>
                </Card>

                {/* Markdown 内容展示 */}
                <Card size="small" title="OCR 识别内容 (Markdown)" extra={<Text type="secondary">Generated by AI</Text>}>
                    {markdownContent ? (
                        <div
                            style={{
                                background: "#f9f9f9",
                                padding: "16px",
                                overflow: "auto",
                                maxHeight: "500px",
                                border: "1px solid #f0f0f0",
                                borderRadius: "4px",
                            }}
                        >
                            <div className="markdown-content">
                                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{markdownContent}</ReactMarkdown>
                            </div>
                        </div>
                    ) : (
                        <Empty description="暂无 Markdown 识别结果" />
                    )}
                </Card>

                {/* 原始 JSON 数据 (可选，折叠或作为调试信息) */}
                {/* <div style={{ marginTop: 16 }}>
                    <Collapse ghost>
                        <Collapse.Panel header="查看原始制表校验数据 (JSON)" key="1">
                            <pre style={{ fontSize: 12, maxHeight: 200, overflow: "auto", background: "#f5f5f5", padding: 8 }}>
                                {JSON.stringify(invoiceData.structured_data, null, 2)}
                            </pre> 
                        </Collapse.Panel>
                    </Collapse>
                </div> */}
            </div>
        );
    };

    const renderContractContent = () => <Empty description="合同查看功能暂未实现" />;

    return (
        <Modal
            title={
                <Space>
                    {type === "invoice" ? <FileTextOutlined /> : <ContainerOutlined />}
                    <span>{type === "invoice" ? "账单凭证查看器" : "合同凭证查看器"}</span>
                </Space>
            }
            open={visible}
            className="xl-modal"
            onCancel={onClose}
            footer={[
                <Button key="refresh" icon={<ReloadOutlined />} onClick={() => loadInvoiceData(invoiceDataSourceId)} disabled={loading || !invoiceDataSourceId} style={{ float: "left" }}>
                    刷新
                </Button>,
                <Button key="close" icon={<CloseOutlined />} onClick={onClose}>
                    关闭 (ESC)
                </Button>,
            ]}
            destroyOnClose
            style={{ top: 40 }}
        >
            {/* 搜索框暂时隐藏，待实现全文检索后开启 */}
            {/* <Search placeholder="搜索凭证内容..." style={{ marginBottom: 16 }} prefix={<SearchOutlined />} allowClear /> */}

            <div style={{ maxHeight: "70vh", overflow: "auto", paddingRight: 4 }}>{type === "invoice" ? renderInvoiceContent() : renderContractContent()}</div>
        </Modal>
    );
};

export default DocumentViewer;
